import type { CSSProperties } from "react";

import { formatCompact } from "@/shared/utils/format";

import { EmptyState } from "./EmptyState";

/** Um item do ranking: `value` já é o número escolhido pela tela. */
export type RankedItem = { key: string; label: string; value: number };

/** Ranking horizontal, os `limit` primeiros com barra proporcional ao
 *  maior e a fatia do TOTAL (de todos os itens, não só dos mostrados).
 *  `noun` é o que se lê depois do número na descrição acessível
 *  ("menções", "de alcance", "chamadas"). Ver o cabeçalho de
 *  `Panel.tsx` pela origem. */
export function RankedBars({ items, noun, limit = 7, formatValue = formatCompact, showShare = true }: {
  items: RankedItem[]; noun: string; limit?: number; formatValue?: (value: number) => string;
  /** `false` tira a fatia "· N%" do total dos itens. Serve a quem conta
   *  coisas que se sobrepõem (uma menção tem vários temas): a soma dos
   *  itens não é o universo, e a tela mostra o percentual que lhe convém
   *  dentro de `formatValue`. */
  showShare?: boolean;
}) {
  const shown = items.slice(0, limit);
  const max = Math.max(...shown.map(item => item.value), 1);
  const total = items.reduce((sum, item) => sum + item.value, 0);
  if (!shown.length) return <EmptyState/>;
  return (
    <ol className="dashboard-ranked-bars">
      {shown.map((item, index) => {
        const share = showShare && total > 0 ? Math.round((item.value / total) * 100) : null;
        return (
          <li
            className="dashboard-ranked-row"
            key={item.key}
            aria-label={`${index + 1}º lugar: ${item.label}, ${formatValue(item.value)} ${noun}${share !== null ? `, ${share}% do total` : ""}`}
          >
            <div className="dashboard-ranked-meta" aria-hidden="true">
              <span><em>{index + 1}</em>{item.label}</span>
              <strong>{formatValue(item.value)}{share !== null && <small>{` · ${share}%`}</small>}</strong>
            </div>
            <div className="dashboard-ranked-track" aria-hidden="true"><span style={{ "--bar-width": `${(item.value / max) * 100}%`, "--bar-opacity": Math.max(0.32, 1 - index * 0.12) } as CSSProperties}/></div>
          </li>
        );
      })}
    </ol>
  );
}
